import React, {type ComponentPropsWithoutRef, useReducer} from 'react';
import {useTranslation} from 'react-i18next';
import {useFlashMessages} from '@ezcater/flash-messages';
import {
  type ApolloError,
  type MutationHookOptions,
  useMutation,
  useLazyQuery,
  gql,
} from '@apollo/client';
import {EzButton} from '@ezcater/recipe';
import formattedOrderNumber from '../../../util/formattedOrderNumber';
import AssignDriverModal from './AssignDriverModal';
import CreateDriverModal from './CreateDriverModal';
import type {
  DeliveryManagementAssignDriverQuery,
  DeliveryManagementAssignDriverQueryVariables,
} from '../../../__generated__/DeliveryManagementAssignDriverQuery';
import type {
  DeliveryManagementAssignDriverMutation,
  DeliveryManagementAssignDriverMutationVariables,
  DeliveryManagementAssignDriverMutation_assignDriver,
} from '../../../__generated__/DeliveryManagementAssignDriverMutation';
import type {
  DeliveryManagementChangeDriverMutation,
  DeliveryManagementChangeDriverMutationVariables,
  DeliveryManagementChangeDriverMutation_changeDriver,
} from '../../../__generated__/DeliveryManagementChangeDriverMutation';
import {SelfAssignSource} from '../../../__generated__/globalTypes';

type Modal = 'AssignDriverModal' | 'CreateDriverModal';

export type Actions =
  | {type: 'MODAL_OPENED'; payload: Modal}
  | {type: 'MODAL_DISMISSED'}
  | {type: 'SUBMIT_SUCCESS'}
  | {type: 'SUBMIT_ERROR'; payload: string | string[]}
  | {type: 'DRIVER_SELECTED'; payload: string};

export type State = {
  errors?: string[];
  openedModal?: Modal;
  selectedDriverId?: string;
};

export const stateReducer = (state: State, action: Actions): State => {
  switch (action.type) {
    case 'MODAL_OPENED': {
      return {...state, openedModal: action.payload};
    }
    case 'MODAL_DISMISSED': {
      return {
        ...state,
        errors: undefined,
        selectedDriverId: undefined,
        openedModal: state.openedModal === 'CreateDriverModal' ? 'AssignDriverModal' : undefined,
      };
    }
    case 'SUBMIT_SUCCESS': {
      return {...state, errors: undefined, openedModal: undefined, selectedDriverId: undefined};
    }
    case 'SUBMIT_ERROR': {
      return {...state, errors: Array.isArray(action.payload) ? action.payload : [action.payload]};
    }
    case 'DRIVER_SELECTED': {
      return {...state, selectedDriverId: action.payload};
    }
    default:
      return state;
  }
};

type OnSubmitCompletedPayload =
  | DeliveryManagementAssignDriverMutation_assignDriver
  | DeliveryManagementChangeDriverMutation_changeDriver
  | null;

type AddDriverModalProps = ComponentPropsWithoutRef<typeof CreateDriverModal>;

type AssignDriverProps = {
  awaitRefetchQueries?: MutationHookOptions['awaitRefetchQueries'];
  deliveryId: string;
  driverId?: string;
  onDriverCreateCompleted?: AddDriverModalProps['onSubmitCompleted'];
  onDriverCreateModalOpen?: () => void;
  onModalDismiss?: () => void;
  onModalOpen?: () => void;
  onSubmitCompleted?: (payload?: OnSubmitCompletedPayload) => void;
  onSubmitError?: (error: ApolloError | Error) => void;
  orderNumber: string;
  refetchQueries?: MutationHookOptions['refetchQueries'];
  source?: 'caterer_accept' | 'food_prep' | 'alert';
  storeId: string;
  stores?: AddDriverModalProps['stores'];
};

const AssignDriver = ({
  awaitRefetchQueries,
  deliveryId,
  driverId: existingDriverId,
  onDriverCreateCompleted,
  onDriverCreateModalOpen,
  onModalDismiss,
  onModalOpen,
  onSubmitCompleted,
  onSubmitError,
  orderNumber,
  refetchQueries,
  source,
  storeId,
  stores = [],
}: AssignDriverProps) => {
  const [t] = useTranslation();
  const {addFlashMessage} = useFlashMessages();
  const [{errors, openedModal, selectedDriverId}, dispatch] = useReducer(stateReducer, {
    errors: undefined,
    openedModal: undefined,
    selectedDriverId: undefined,
  });
  const [executeAssignDriverDeliveryQuery, assignDriverDeliveryQueryResult] = useLazyQuery<
    DeliveryManagementAssignDriverQuery,
    DeliveryManagementAssignDriverQueryVariables
  >(AssignDriverDeliveryQuery, {
    errorPolicy: 'all',
    onCompleted: data => {
      if (data?.delivery?.potentialDrivers.length) {
        handleAssignDriverModalOpen();
      } else {
        handleCreateDriverModalOpen();
      }
    },
  });
  const [assignDriverMutation] = useMutation<
    DeliveryManagementAssignDriverMutation,
    DeliveryManagementAssignDriverMutationVariables
  >(AssignDriverMutation);
  const [changeDriverMutation] = useMutation<
    DeliveryManagementChangeDriverMutation,
    DeliveryManagementChangeDriverMutationVariables
  >(ChangeDriverMutation);

  const potentialDrivers = assignDriverDeliveryQueryResult.data?.delivery.potentialDrivers;
  const hasPotentialDrivers = !!potentialDrivers?.length;

  const executeAssignOrChangeDriverMutation = async (targetDriverId: string) => {
    if (existingDriverId) {
      const changeDriverResult = await changeDriverMutation({
        variables: {
          input: {
            deliveryId,
            unassignDriverId: existingDriverId,
            assignDriverId: targetDriverId,
          },
        },
        refetchQueries,
        awaitRefetchQueries,
      });

      return changeDriverResult?.data?.changeDriver;
    }

    const assignDriverResult = await assignDriverMutation({
      variables: {
        input: {
          deliveryId,
          driverId: targetDriverId,
          source: source ? SelfAssignSource[source] : undefined,
        },
      },
      refetchQueries,
      awaitRefetchQueries,
    });

    return assignDriverResult?.data?.assignDriver;
  };

  const handleAssignOrChangeDriverSubmit = async (payload: {driverId: string}) => {
    try {
      const result = await executeAssignOrChangeDriverMutation(payload.driverId);

      if (result?.errors?.length) {
        dispatch({type: 'SUBMIT_ERROR', payload: result.errors});
      } else {
        addFlashMessage({
          type: 'success',
          message: t('components.deliveryManagement.assignDriver.flashMessages.submitSuccess', {
            orderNumber: formattedOrderNumber(orderNumber),
          }),
        });

        dispatch({type: 'SUBMIT_SUCCESS'});
      }

      await onSubmitCompleted?.(result);
    } catch (e) {
      if (e instanceof Error) {
        handleSubmitError();
        await onSubmitError?.(e);
      } else {
        throw e;
      }
    }
  };

  const handleAssignDriverModalOpen = () => {
    dispatch({type: 'MODAL_OPENED', payload: 'AssignDriverModal'});
    return onModalOpen?.();
  };

  const handleCreateDriverModalOpen = () => {
    dispatch({type: 'MODAL_OPENED', payload: 'CreateDriverModal'});
    return onDriverCreateModalOpen?.();
  };

  const handleModalDismiss = () => {
    dispatch({type: 'MODAL_DISMISSED'});
    return onModalDismiss?.();
  };

  const handleModalOpenButtonClick = async () => {
    if (assignDriverDeliveryQueryResult.called) {
      if (potentialDrivers?.length) {
        handleAssignDriverModalOpen();
      } else {
        handleCreateDriverModalOpen();
      }
    } else {
      executeAssignDriverDeliveryQuery({variables: {id: deliveryId}});
    }
  };

  const handleCreateDriverSubmitCompleted: AddDriverModalProps['onSubmitCompleted'] =
    async payload => {
      if (payload?.errors?.length) {
        dispatch({type: 'SUBMIT_ERROR', payload: payload.errors});
        await onDriverCreateCompleted?.(payload);
      } else if (payload?.driver) {
        dispatch({type: 'DRIVER_SELECTED', payload: payload.driver.id});
        await onDriverCreateCompleted?.(payload);
        await handleAssignOrChangeDriverSubmit({driverId: payload.driver.id});
      }
    };

  const handleSubmitError = () => {
    dispatch({
      type: 'SUBMIT_ERROR',
      payload: [t('components.deliveryManagement.assignDriver.serverErrorMessage')],
    });
  };

  const handleAssignDriverChange = (newDriverId: string) => {
    dispatch({type: 'DRIVER_SELECTED', payload: newDriverId});
  };

  return (
    <>
      <EzButton legacy use="tertiary" onClick={handleModalOpenButtonClick}>
        {existingDriverId
          ? t('components.deliveryManagement.assignDriver.changeDriverModalToggler')
          : t('components.deliveryManagement.assignDriver.assignDriverModalToggler')}
      </EzButton>
      <AssignDriverModal
        driverId={selectedDriverId || existingDriverId}
        errors={errors}
        isLoading={assignDriverDeliveryQueryResult.loading}
        isOpen={openedModal === 'AssignDriverModal' || assignDriverDeliveryQueryResult.loading}
        onDismiss={handleModalDismiss}
        onDriverChange={handleAssignDriverChange}
        onDriverCreateModalOpen={handleCreateDriverModalOpen}
        onSubmit={handleAssignOrChangeDriverSubmit}
        orderNumber={orderNumber}
        potentialDrivers={potentialDrivers}
      />
      <CreateDriverModal
        currentStoreId={storeId}
        errors={errors}
        isAdditional={hasPotentialDrivers}
        isOpen={openedModal === 'CreateDriverModal'}
        onDismiss={handleModalDismiss}
        onSubmitCompleted={handleCreateDriverSubmitCompleted}
        onSubmitError={handleSubmitError}
        orderNumber={orderNumber}
        stores={stores}
      />
    </>
  );
};

export const AssignDriverDeliveryQuery = gql`
  query DeliveryManagementAssignDriverQuery($id: ID!) {
    delivery: dmDelivery(id: $id) {
      id
      potentialDrivers {
        id
        ...DeliveryManagementAssignDriverModalDriverFragment
      }
    }
  }
  ${AssignDriverModal.fragments.driver}
`;

export const AssignDriverMutation = gql`
  mutation DeliveryManagementAssignDriverMutation($input: AssignDriverInput!) {
    assignDriver(input: $input) {
      delivery {
        id
        status
        drivers {
          id
        }
      }
      errors
    }
  }
`;

export const ChangeDriverMutation = gql`
  mutation DeliveryManagementChangeDriverMutation($input: ChangeDriverInput!) {
    changeDriver(input: $input) {
      delivery {
        id
        status
        drivers {
          id
        }
      }
      errors
    }
  }
`;

export default AssignDriver;
